// src/ShareResult.tsx
//
// Sdílení výsledku jako obrázek (nice-to-have po redesignu, rozhodnuto
// 2. 10. 2026): u závodu, kde má přihlášený střelec výsledek, tlačítko
// „Sdílet můj výsledek" → náhled karty (závod, umístění celkem i v kategorii,
// body, % z vítěze) → obrázek PNG (html-to-image) → systémové sdílení
// (Web Share API se souborem), jinak stažení; bez systémového sdílení aspoň
// zkopírování textu. Ve WebView obalu aplikace LKS (Android WebView nemá
// Web Share API, stažení souboru ani uložení obrázku podržením) jde jen
// text – dialog to říká a odkazuje na prohlížeč.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "./ui/ConfirmDialog";
import { LoadingState } from "./ui/AsyncState";
import { useToast } from "./ui/Toast";
import { categoryLabel, type ResultCategoryId } from "./utils/categories";
import { copyToClipboard } from "./utils/clipboard";
import { formatCzDate } from "./utils/dates";
import { fmtFixed } from "./utils/formatPoints";
import { reportWarning } from "./utils/reportError";

export interface ShareResultData {
  raceName: string;
  /** "YYYY-MM-DD". */
  date: string;
  disciplineLabel: string;
  shooterName: string;
  rank: number;
  of: number;
  categoryId: ResultCategoryId | null;
  categoryRank: number | null;
  categoryOf: number | null;
  points: number;
  /** Desetinná místa bodů v závodě (pointsDecimals – jako tabulka). */
  pointsDecimals: number;
  /** % z vítěze závodu. */
  percent: number;
  /** Pořadí při shodě bodů určil rozstřel. */
  shootOff: boolean;
}

/** Text ke sdílení (bez obrázku nebo jako jeho popisek). */
export function shareResultText(d: ShareResultData): string {
  const category =
    d.categoryId && d.categoryRank && d.categoryOf
      ? `, ${categoryLabel(d.categoryId)} ${d.categoryRank}. z ${d.categoryOf}`
      : "";
  return (
    `${d.raceName} (${formatCzDate(d.date)}): ${d.rank}. místo z ${d.of}${category} – ` +
    `${fmtFixed(d.points, d.pointsDecimals)} b., ${fmtFixed(d.percent, 1)} % z vítěze`
  );
}

/** Název souboru bez diakritiky: "lks-vysledek-2026-09-26.png". */
const fileNameOf = (d: ShareResultData): string => `lks-vysledek-${d.date || "zavod"}.png`;

// Karta se kreslí do obrázku – jen inline styly a systémové písmo (ikony
// z písma bootstrap-icons by html-to-image musel stahovat a vkládat).
const CARD_STYLE: React.CSSProperties = {
  width: 360,
  boxSizing: "border-box",
  background: "#ffffff",
  color: "#1b1f1d",
  fontFamily: "var(--lks-font, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif)",
  borderRadius: 16,
  overflow: "hidden",
  border: "1px solid #d5dcd8",
};

const ShareCard = React.forwardRef<HTMLDivElement, { data: ShareResultData }>(({ data }, ref) => (
  <div ref={ref} style={CARD_STYLE}>
    <div style={{ background: "var(--lks-brand, #004d40)", color: "#ffffff", padding: "14px 18px" }}>
      <div style={{ fontSize: 13, letterSpacing: 1, textTransform: "uppercase", opacity: 0.85 }}>
        LKS · {data.disciplineLabel}
      </div>
      <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.25, marginTop: 4, overflowWrap: "anywhere" }}>
        {data.raceName}
      </div>
      <div style={{ fontSize: 14, opacity: 0.85, marginTop: 2 }}>{formatCzDate(data.date)}</div>
    </div>
    <div style={{ padding: "16px 18px 18px" }}>
      <div style={{ fontSize: 17, fontWeight: 600, overflowWrap: "anywhere" }}>{data.shooterName}</div>
      <div style={{ display: "flex", alignItems: "baseline", marginTop: 8 }}>
        <span style={{ fontSize: 46, fontWeight: 800, lineHeight: 1, color: "var(--lks-brand, #004d40)" }}>
          {data.rank}.
        </span>
        <span style={{ fontSize: 16, marginLeft: 8 }}>místo z {data.of}</span>
        {data.shootOff && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 12,
              fontWeight: 700,
              background: "#ffc107",
              color: "#1b1f1d",
              borderRadius: 6,
              padding: "2px 6px",
            }}
          >
            R
          </span>
        )}
      </div>
      {data.categoryId && data.categoryRank && data.categoryOf ? (
        <div style={{ fontSize: 15, marginTop: 6 }}>
          {categoryLabel(data.categoryId)}: <strong>{data.categoryRank}.</strong> místo z {data.categoryOf}
        </div>
      ) : null}
      <div style={{ display: "flex", marginTop: 14 }}>
        <div style={{ flex: "1 1 0", background: "#f1f4f2", borderRadius: 10, padding: "8px 10px", marginRight: 8 }}>
          <div style={{ fontSize: 12, color: "#5c6762" }}>Body</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtFixed(data.points, data.pointsDecimals)}</div>
        </div>
        <div style={{ flex: "1 1 0", background: "#f1f4f2", borderRadius: 10, padding: "8px 10px" }}>
          <div style={{ fontSize: 12, color: "#5c6762" }}>% z vítěze</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{fmtFixed(data.percent, 1)} %</div>
        </div>
      </div>
      {data.shootOff && (
        <div style={{ fontSize: 12, color: "#5c6762", marginTop: 8 }}>R = pořadí při shodě bodů určil rozstřel</div>
      )}
    </div>
  </div>
));
ShareCard.displayName = "ShareCard";

type HtmlToImage = typeof import("html-to-image");
let htmlToImage: Promise<HtmlToImage> | null = null;

/**
 * html-to-image se stahuje s tlačítkem sdílení, ne s aplikací (úklid
 * a rychlost) – hned po zobrazení tlačítka, takže obrázek jde vytvořit i když
 * signál mezitím vypadne. Po chybě se při dalším pokusu stahuje znovu.
 * Samotné systémové sdílení (navigator.share) běží až nad hotovým obrázkem,
 * synchronně v kliknutí – gesto uživatele se neztratí.
 */
export function loadHtmlToImage(): Promise<HtmlToImage> {
  if (!htmlToImage) {
    const loading = import(/* webpackChunkName: "share-image" */ "html-to-image");
    htmlToImage = loading;
    loading.catch(() => {
      if (htmlToImage === loading) htmlToImage = null;
    });
  }
  return htmlToImage;
}

type ShareSupport = "files" | "text" | "none";

function shareSupportFor(file: File | null): ShareSupport {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") return "none";
  try {
    if (file && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
      return "files";
    }
  } catch {
    // canShare může na starších prohlížečích vyhodit – sdílí se aspoň text
  }
  return "text";
}

export interface ShareResultButtonProps {
  data: ShareResultData;
  /** Nativní obal (WebView) – stahování souboru tam nemusí fungovat. */
  nativeApp?: boolean;
}

export const ShareResultButton: React.FC<ShareResultButtonProps> = ({ data, nativeApp = false }) => {
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [image, setImage] = useState<{ blob: Blob; url: string } | null>(null);
  const [failed, setFailed] = useState(false);

  // Knihovnu na obrázek stáhnout hned s tlačítkem (bez vlivu na stránku)
  useEffect(() => {
    loadHtmlToImage().catch(() => undefined);
  }, []);

  // Obrázek se vytvoří po otevření (karta už je v DOM), URL se uvolní po zavření
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    setFailed(false);
    // Až po vykreslení karty v dialogu (portál) – další snímek
    const timer = window.setTimeout(() => {
      const node = cardRef.current;
      if (!node) return;
      // html-to-image stažené předem (loadHtmlToImage); když se nestáhne
      // (offline), zbude stejně jako při chybě obrázku sdílení textu
      loadHtmlToImage()
        .then(({ toBlob }) =>
          cancelled ? null : toBlob(node, { pixelRatio: 3, skipFonts: true, backgroundColor: "#ffffff" })
        )
        .then(
          (blob) => {
            if (cancelled) return;
            if (!blob) {
              setFailed(true);
              return;
            }
            createdUrl = URL.createObjectURL(blob);
            setImage({ blob, url: createdUrl });
          },
          (err: unknown) => {
            reportWarning("ShareResult.toBlob", err);
            if (!cancelled) setFailed(true);
          }
        );
    }, 50);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      setImage(null);
    };
  }, [open]);

  const fileName = fileNameOf(data);
  const file = useMemo(
    () =>
      image && typeof File === "function"
        ? new File([image.blob], fileName, { type: "image/png" })
        : null,
    [image, fileName]
  );
  const support = shareSupportFor(file);
  const text = shareResultText(data);

  const handleShare = async () => {
    try {
      if (support === "files" && file) {
        await navigator.share({ files: [file], title: data.raceName, text });
      } else {
        await navigator.share({ title: data.raceName, text });
      }
    } catch (err) {
      // Zrušení sdílení uživatelem není chyba
      if (err && (err as { name?: string }).name === "AbortError") return;
      reportWarning("ShareResult.share", err);
      showToast("Sdílení se nepodařilo – obrázek můžete stáhnout nebo uložit.", "warning");
    }
  };

  const handleCopyText = async () => {
    const ok = await copyToClipboard(text);
    showToast(ok ? "Text výsledku zkopírován." : "Text se nepodařilo zkopírovat.", ok ? "success" : "warning");
  };

  const handleDownload = () => {
    if (!image) return;
    const a = document.createElement("a");
    a.href = image.url;
    a.download = fileName;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const canShareImage = support === "files" || !nativeApp;

  return (
    <>
      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setOpen(true)}>
        <i className="bi bi-share me-1" aria-hidden="true" />
        Sdílet můj výsledek
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Sdílet výsledek"
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setOpen(false)}>
              Zavřít
            </button>
            {!nativeApp && (
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleDownload}
                disabled={!image}
              >
                <i className="bi bi-download me-1" aria-hidden="true" />
                Stáhnout
              </button>
            )}
            {support !== "none" ? (
              <button type="button" className="btn btn-primary" onClick={handleShare} disabled={!image && !failed}>
                <i className="bi bi-share me-1" aria-hidden="true" />
                Sdílet
              </button>
            ) : (
              // Bez systémového sdílení (WebView obal, desktop) aspoň text
              <button type="button" className="btn btn-primary" onClick={handleCopyText}>
                <i className="bi bi-clipboard me-1" aria-hidden="true" />
                Kopírovat text
              </button>
            )}
          </>
        }
      >
        {/* Karta pro snímek mimo obraz v plné šířce 360 px (v úzkém dialogu by
            byla oříznutá); v dialogu se ukazuje až hotový obrázek */}
        {!image && (
          <div aria-hidden="true" style={{ position: "fixed", left: -10000, top: 0 }}>
            <ShareCard ref={cardRef} data={data} />
          </div>
        )}
        <div className="d-flex justify-content-center">
          {image ? (
            <img
              src={image.url}
              alt={text}
              style={{ width: 360, maxWidth: "100%", height: "auto", borderRadius: 16 }}
            />
          ) : failed ? null : (
            <LoadingState text="Připravuji obrázek…" />
          )}
        </div>
        {failed ? (
          <p className="small text-body-secondary mt-2 mb-0">
            Obrázek se nepodařilo vytvořit – sdílet jde aspoň text výsledku:
            <span className="d-block mt-1 text-body">{text}</span>
          </p>
        ) : !canShareImage ? (
          <p className="small text-body-secondary mt-2 mb-0">
            V aplikaci se obrázek uložit ani sdílet nedá – otevřete Výsledky
            v prohlížeči telefonu. Text výsledku můžete zkopírovat.
          </p>
        ) : null}
      </Modal>
    </>
  );
};

export default ShareResultButton;
